import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Action } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import * as InternetSettingsActions from './../actions/internet-settings.actions';
import {
  AddMacIpAddress,
  AddMacIpAddressFailure,
  AddMacIpAddressSuccess,
  DeleteMacIpAddress,
  DeleteMacIpAddressFailure,
  DeleteMacIpAddressSuccess,
  GetMacIpAddress,
  GetMacIpAddressFailure,
  GetMacIpAddressSuccess,
  GetModemSafetySettings,
  GetModemSafetySettingsFailure,
  GetModemSafetySettingsSuccess,
  GetWifiSsid,
  GetWifiSsidFailure,
  GetWifiSsidSuccess,
  UpdateMacIpAddress,
  UpdateMacIpAddressFailure,
  UpdateMacIpAddressSuccess,
  UpdateModemSafetySettings,
  UpdateModemSafetySettingsFailure,
  UpdateModemSafetySettingsSuccess,
  UpdatePassword,
  UpdatePasswordFailed,
  UpdatePasswordSuccess,
  UpdateSharedIpSettings,
  UpdateSharedIpSettingsFailure,
  UpdateSharedIpSettingsSuccess,
  UpdateWifiSsid,
  UpdateWifiSsidFailure,
  UpdateWifiSsidSuccess,
} from './../actions/internet-settings.actions';
import { catchError, map, switchMap } from 'rxjs/operators';
import { InternetSettingsService } from '../../services/internet-settings.service';
import { IEntity } from '@proximus/entity-manager';
import { TechnicalError } from '@dch/products-business-logic';

@Injectable({
  providedIn: 'root',
})
export class InternetSettingsEffects {
  @Effect()
  updatePassword$: Observable<Action> = this.actions$.pipe(
    ofType(InternetSettingsActions.UPDATE_PASSWORD),
    switchMap((action: UpdatePassword) =>
      this.internetSettingsService.updateInternetPassword(action.payload).pipe(
        map(() => new UpdatePasswordSuccess({})),
        catchError(() => of(new UpdatePasswordFailed({}))),
      ),
    ),
  );

  @Effect()
  getModemSafety$: Observable<Action> = this.actions$.pipe(
    ofType(InternetSettingsActions.GET_MODEM_SAFETY),
    switchMap((action: GetModemSafetySettings) =>
      this.internetSettingsService.getModemSafety(action.payload).pipe(
        map(
          (iEntity: IEntity<any>) =>
            new GetModemSafetySettingsSuccess(iEntity.value),
        ),
        catchError((error: IEntity<TechnicalError>) =>
          of(new GetModemSafetySettingsFailure(true)),
        ),
      ),
    ),
  );

  @Effect()
  updateModemSafety$: Observable<Action> = this.actions$.pipe(
    ofType(InternetSettingsActions.UPDATE_MODEM_SAFETY),
    switchMap((action: UpdateModemSafetySettings) =>
      this.internetSettingsService.updateModemSafety(action.payload).pipe(
        map(
          (iEntity: IEntity<any>) =>
            new UpdateModemSafetySettingsSuccess(
              action.payload.technicalProfile,
            ),
        ),
        catchError((error: IEntity<TechnicalError>) =>
          of(new UpdateModemSafetySettingsFailure(true)),
        ),
      ),
    ),
  );

  @Effect()
  updateSharedIp$: Observable<Action> = this.actions$.pipe(
    ofType(InternetSettingsActions.UPDATE_SHARED_IP),
    switchMap((action: UpdateSharedIpSettings) =>
      this.internetSettingsService.updateCgnSettings(action.payload).pipe(
        map(
          (iEntity: IEntity<any>) =>
            new UpdateSharedIpSettingsSuccess(iEntity.value),
        ),
        catchError((error: IEntity<TechnicalError>) =>
          of(new UpdateSharedIpSettingsFailure(true)),
        ),
      ),
    ),
  );

  @Effect()
  updateMacIpAddress$: Observable<Action> = this.actions$.pipe(
    ofType(InternetSettingsActions.UPDATE_MAC_IP_ADDRESS),
    switchMap((action: UpdateMacIpAddress) =>
      this.internetSettingsService.updateMacIpAddress(action.payload).pipe(
        map(
          (iEntity: IEntity<any>) =>
            new UpdateMacIpAddressSuccess(iEntity.value),
        ),
        catchError((error: IEntity<TechnicalError>) =>
          of(new UpdateMacIpAddressFailure(true)),
        ),
      ),
    ),
  );

  @Effect()
  addMacIpAddress$: Observable<Action> = this.actions$.pipe(
    ofType(InternetSettingsActions.ADD_MAC_IP_ADDRESS),
    switchMap((action: AddMacIpAddress) =>
      this.internetSettingsService.addMacIpAddress(action.payload).pipe(
        map(
          (iEntity: IEntity<any>) => new AddMacIpAddressSuccess(iEntity.value),
        ),
        catchError((error: IEntity<TechnicalError>) =>
          of(new AddMacIpAddressFailure(true)),
        ),
      ),
    ),
  );

  @Effect()
  getMacIpAddress$: Observable<Action> = this.actions$.pipe(
    ofType(InternetSettingsActions.GET_MAC_IP_ADDRESS),
    switchMap((action: GetMacIpAddress) =>
      this.internetSettingsService.getMacIpAddress(action.payload).pipe(
        map(
          (iEntity: IEntity<any>) => new GetMacIpAddressSuccess(iEntity.value),
        ),
        catchError((error: IEntity<TechnicalError>) =>
          of(new GetMacIpAddressFailure(true)),
        ),
      ),
    ),
  );

  @Effect()
  deleteMacIpAddress$: Observable<Action> = this.actions$.pipe(
    ofType(InternetSettingsActions.DELETE_MAC_IP_ADDRESS),
    switchMap((action: DeleteMacIpAddress) =>
      this.internetSettingsService.deleteMacIpAddress(action.payload).pipe(
        map(
          (iEntity: IEntity<any>) =>
            new DeleteMacIpAddressSuccess(iEntity.value),
        ),
        catchError((error: IEntity<TechnicalError>) =>
          of(new DeleteMacIpAddressFailure(true)),
        ),
      ),
    ),
  );

  @Effect()
  getWifiSsid$: Observable<Action> = this.actions$.pipe(
    ofType(InternetSettingsActions.GET_WIFI_SSID),
    switchMap((action: GetWifiSsid) =>
      this.internetSettingsService.getWifiSsid(action.payload).pipe(
        map((iEntity: IEntity<any>) => new GetWifiSsidSuccess(iEntity.value)),
        catchError((error: IEntity<TechnicalError>) =>
          of(new GetWifiSsidFailure(true)),
        ),
      ),
    ),
  );

  @Effect()
  updateWifiSsid$: Observable<Action> = this.actions$.pipe(
    ofType(InternetSettingsActions.UPDATE_WIFI_SSID),
    switchMap((action: UpdateWifiSsid) =>
      this.internetSettingsService.updateWifiSsid(action.payload).pipe(
        map(() => new UpdateWifiSsidSuccess(action.payload.wifiSsid)),
        catchError(() => of(new UpdateWifiSsidFailure(true))),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private internetSettingsService: InternetSettingsService,
  ) {}
}
